// Escape results: stops the game's play timer (same localStorage key as s-game-timer), shows the time,
// hints, solutions and date, and fills the hidden fields of the leaderboard contact form.
// data-mode="inline" (finale pages) finishes on load; data-mode="dialog" opens on the --escape command.
class SEscapeResults extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('command', (event) => this.#handle(event.command), { signal });
    // Fallback for browsers without invoker commands: buttons anywhere that target this element.
    if (!('CommandEvent' in window)) {
      document.addEventListener('click', (event) => {
        const button = event.target.closest(`button[commandfor="${this.id}"]`);
        if (button) this.#handle(button.getAttribute('command'));
      }, { signal });
    }
    if (this.dataset.mode === 'inline') {
      this.#finish();
    } else if (this.querySelector('[data-posted-success]')) {
      this.#fill(this.#load());
      this.querySelector('dialog')?.showModal();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #storageKey() {
    return `mysterylocks-timer-${this.dataset.key || 'game'}`;
  }

  #load() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.#storageKey));
      if (saved && typeof saved.elapsed === 'number') return saved;
    } catch (error) {
      // Private browsing or bad data.
    }
    return { running: false, startedAt: 0, elapsed: 0, hints: [], solutions: [] };
  }

  #handle(command) {
    if (command === '--escape') {
      this.#finish();
      this.querySelector('dialog')?.showModal();
    } else if (command === '--close') {
      this.querySelector('dialog')?.close();
    }
  }

  #finish() {
    let state = this.#load();
    if (!state.finished && (state.elapsed > 0 || state.running)) {
      const elapsed = state.running ? state.elapsed + (Date.now() - state.startedAt) : state.elapsed;
      state = { ...state, running: false, startedAt: 0, elapsed, finished: true, finishedAt: new Date().toISOString().slice(0, 10) };
      try {
        localStorage.setItem(this.#storageKey, JSON.stringify(state));
      } catch (error) {
        // Storage unavailable: results still show for this visit.
      }
      window.dispatchEvent(new CustomEvent('mysterylocks:timer-sync', { detail: { key: this.dataset.key } }));
    }
    this.#fill(state);
  }

  #formatTime(ms) {
    const total = Math.floor(ms / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const seconds = String(total % 60).padStart(2, '0');
    return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
  }

  #setText(selector, text) {
    this.querySelectorAll(selector).forEach((node) => {
      if (node.textContent !== text) node.textContent = text;
    });
  }

  #setField(name, value) {
    const input = this.querySelector(`input[name="contact[${name}]"]`);
    if (input && input.value !== value) input.value = value;
  }

  #fill(state) {
    const played = state.elapsed > 0;
    const time = this.#formatTime(state.elapsed);
    const hints = String(state.hints?.length || 0);
    const solutions = String(state.solutions?.length || 0);
    const isoDate = state.finishedAt || new Date().toISOString().slice(0, 10);
    const date = new Date(`${isoDate}T12:00:00`).toLocaleDateString(document.documentElement.lang || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    this.#setText('[data-result-time]', time);
    this.#setText('[data-result-hints]', hints);
    this.#setText('[data-result-solutions]', solutions);
    this.#setText('[data-result-date]', date);
    this.#setField('Time', time);
    this.#setField('Hints used', hints);
    this.#setField('Solutions used', solutions);
    this.#setField('Date played', isoDate);
    this.querySelectorAll('[data-results-played]').forEach((node) => {
      if (node.hidden === played) node.hidden = !played;
    });
    this.querySelectorAll('[data-results-empty]').forEach((node) => {
      if (node.hidden !== played) node.hidden = played;
    });
  }
}

if (!customElements.get('s-escape-results')) customElements.define('s-escape-results', SEscapeResults);
